import React, { useState, useMemo } from 'react';
import { Clock, MapPin, Check, X, Filter, Calendar, User, ExternalLink } from 'lucide-react';
import { useTimeEntries } from '../hooks/useTimeEntries';
import { useEmployees } from '../hooks/useEmployees';
import { useAuth } from '../hooks/useAuth';
import { usePayPeriodSettings } from '../hooks/usePayPeriodSettings';
import { getCurrentPayPeriod, getAllPayPeriods } from '../utils/payrollPeriodCalculator';
import type { TimeEntry } from '../types';

const Timesheet: React.FC = () => {
  const { user } = useAuth();
  const { employees } = useEmployees();
  const { settings: payPeriodSettings } = usePayPeriodSettings();
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  const [customPeriodStart, setCustomPeriodStart] = useState('');
  const [customPeriodEnd, setCustomPeriodEnd] = useState('');

  const payPeriods = useMemo(() => {
    if (!payPeriodSettings) return [];
    return getAllPayPeriods(
      payPeriodSettings.frequency,
      payPeriodSettings.periodStart,
      payPeriodSettings.payday,
      6
    );
  }, [payPeriodSettings]);

  const currentPeriod = useMemo(() => {
    if (!payPeriodSettings) return null;
    return getCurrentPayPeriod(
      payPeriodSettings.frequency,
      payPeriodSettings.periodStart,
      payPeriodSettings.payday
    );
  }, [payPeriodSettings]);

  const periodFilter = useMemo(() => {
    if (selectedPeriod === 'all') return { start: undefined, end: undefined };
    if (selectedPeriod === 'current' && currentPeriod) {
      return { start: currentPeriod.start, end: currentPeriod.end };
    }
    if (selectedPeriod === 'custom') {
      return { start: customPeriodStart, end: customPeriodEnd };
    }
    const period = payPeriods.find(p => `${p.start}-${p.end}` === selectedPeriod);
    return period ? { start: period.start, end: period.end } : { start: undefined, end: undefined };
  }, [selectedPeriod, currentPeriod, payPeriods, customPeriodStart, customPeriodEnd]);

  const { timeEntries, loading, approveTimeEntry, rejectTimeEntry } = useTimeEntries(
    selectedEmployee === 'all' ? undefined : selectedEmployee,
    periodFilter.start,
    periodFilter.end
  );

  const filteredEntries = useMemo(() => {
    let filtered = [...timeEntries];

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(entry => entry.status === selectedStatus);
    }

    return filtered;
  }, [timeEntries, selectedStatus]);

  const hourlyEmployees = useMemo(() => {
    return employees.filter(emp => emp.paymentType === 'hourly' && emp.isActive);
  }, [employees]);

  const summaryByEmployee = useMemo(() => {
    const summary: Record<string, { totalHours: number; entries: number; pending: number }> = {};

    filteredEntries.forEach(entry => {
      if (!summary[entry.employeeId]) {
        summary[entry.employeeId] = { totalHours: 0, entries: 0, pending: 0 };
      }
      summary[entry.employeeId].totalHours += entry.hoursWorked || 0;
      summary[entry.employeeId].entries += 1;
      if (entry.status === 'pending') {
        summary[entry.employeeId].pending += 1;
      }
    });

    return summary;
  }, [filteredEntries]);

  const handleApprove = async (entryId: string) => {
    if (!user?.employeeId) return;
    try {
      await approveTimeEntry(entryId, user.employeeId);
    } catch (err) {
      console.error('Error approving entry:', err);
    }
  };

  const handleReject = async (entryId: string) => {
    if (!user?.employeeId) return;
    const notes = prompt('Enter reason for rejection (optional):');
    try {
      await rejectTimeEntry(entryId, user.employeeId, notes || undefined);
    } catch (err) {
      console.error('Error rejecting entry:', err);
    }
  };

  const openInMaps = (latitude: number, longitude: number) => {
    window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading timesheets...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Timesheets</h2>
        <Clock className="h-8 w-8 text-blue-600" />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Filter className="h-5 w-5 mr-2" />
          Filters
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="h-4 w-4 inline-block mr-1" />
              Employee
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Employees</option>
              {hourlyEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline-block mr-1" />
              Pay Period
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Periods</option>
              <option value="current">Current Period</option>
              {payPeriods.map(period => (
                <option key={`${period.start}-${period.end}`} value={`${period.start}-${period.end}`}>
                  {new Date(period.start).toLocaleDateString()} - {new Date(period.end).toLocaleDateString()}
                </option>
              ))}
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {selectedPeriod === 'custom' && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={customPeriodStart}
                onChange={(e) => setCustomPeriodStart(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={customPeriodEnd}
                onChange={(e) => setCustomPeriodEnd(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Hours</div>
          <div className="text-3xl font-bold text-blue-600">
            {filteredEntries.reduce((sum, entry) => sum + (entry.hoursWorked || 0), 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Pending Approval</div>
          <div className="text-3xl font-bold text-yellow-600">
            {filteredEntries.filter(e => e.status === 'pending').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Entries</div>
          <div className="text-3xl font-bold text-gray-900">
            {filteredEntries.length}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clock In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clock Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getEmployeeName(entry.employeeId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(entry.clockInTime).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(entry.clockInTime).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {entry.clockOutTime ? (
                      <>
                        <div className="text-sm text-gray-900">
                          {new Date(entry.clockOutTime).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(entry.clockOutTime).toLocaleTimeString()}
                        </div>
                      </>
                    ) : (
                      <span className="text-sm text-yellow-600 font-medium">In Progress</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {entry.hoursWorked ? `${entry.hoursWorked.toFixed(2)} hrs` : '--'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {entry.clockInLatitude && entry.clockInLongitude && (
                        <button
                          onClick={() => openInMaps(entry.clockInLatitude!, entry.clockInLongitude!)}
                          className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          Clock In
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </button>
                      )}
                      {entry.clockOutLatitude && entry.clockOutLongitude && (
                        <button
                          onClick={() => openInMaps(entry.clockOutLatitude!, entry.clockOutLongitude!)}
                          className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          Clock Out
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </button>
                      )}
                      {!entry.clockInLatitude && (
                        <span className="text-xs text-gray-400">No location</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      entry.status === 'approved' ? 'bg-green-100 text-green-800' :
                      entry.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {entry.status === 'pending' && entry.clockOutTime && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(entry.id)}
                          className="inline-flex items-center p-1 border border-transparent rounded-full text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          title="Approve"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleReject(entry.id)}
                          className="inline-flex items-center p-1 border border-transparent rounded-full text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          title="Reject"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {entry.status !== 'pending' && (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredEntries.length === 0 && (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No time entries found</p>
            </div>
          )}
        </div>
      </div>

      {selectedEmployee === 'all' && Object.keys(summaryByEmployee).length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Summary by Employee</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entries
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(summaryByEmployee).map(([employeeId, summary]) => (
                  <tr key={employeeId}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {getEmployeeName(employeeId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                      {summary.totalHours.toFixed(2)} hrs
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {summary.entries}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {summary.pending > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {summary.pending}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">--</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timesheet;
